/*
 * Stratus — homepage Story Row carousel (st-home-stories.js)
 *
 * Loaded with `defer` by sections/st-home-stories.liquid. No library. A custom element, <st-home-stories>, wraps the
 * card list ([data-st-stories-list]) and its two buttons ([data-st-stories-prev], [data-st-stories-next]):
 * - a click scrolls by one card plus the gap (smooth, or instant under prefers-reduced-motion: reduce);
 * - each button is shown only when there is somewhere to go ([hidden] otherwise), recomputed on scroll (once per
 *   animation frame) and whenever the list resizes;
 * - when the focused button hides at its end, focus moves to the other button, so it is never dropped.
 * Without JavaScript the buttons stay hidden and the row still scrolls and swipes. Whether the buttons show at all
 * (phones, touch tablets) is decided in st-home-stories.css.
 */
(() => {
  if (customElements.get('st-home-stories')) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class StHomeStories extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('[data-st-stories-list]');
      this.prev = this.querySelector('[data-st-stories-prev]');
      this.next = this.querySelector('[data-st-stories-next]');
      this.ready = false;
      if (!this.list || !this.prev || !this.next) return;

      this.frame = 0;
      this.onScroll = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = 0;
          this.update();
        });
      };
      this.onPrev = () => this.go(-1);
      this.onNext = () => this.go(1);

      this.list.addEventListener('scroll', this.onScroll, { passive: true });
      this.prev.addEventListener('click', this.onPrev);
      this.next.addEventListener('click', this.onNext);

      if ('ResizeObserver' in window) {
        this.observer = new ResizeObserver(this.onScroll);
        this.observer.observe(this.list);
      } else {
        window.addEventListener('resize', this.onScroll, { passive: true });
      }

      this.ready = true;
      this.update();
    }

    disconnectedCallback() {
      if (!this.ready) return;
      this.ready = false;
      this.list.removeEventListener('scroll', this.onScroll);
      this.prev.removeEventListener('click', this.onPrev);
      this.next.removeEventListener('click', this.onNext);
      if (this.observer) this.observer.disconnect();
      else window.removeEventListener('resize', this.onScroll);
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = 0;
    }

    step() {
      const card = this.list.firstElementChild;
      if (!card) return 0;
      const gap = parseFloat(getComputedStyle(this.list).columnGap) || 0;
      return card.getBoundingClientRect().width + gap;
    }

    go(direction) {
      this.list.scrollBy({
        left: direction * this.step(),
        behavior: reducedMotion.matches ? 'auto' : 'smooth',
      });
    }

    update() {
      const max = this.list.scrollWidth - this.list.clientWidth;
      const position = Math.abs(this.list.scrollLeft);
      this.toggle(this.prev, this.next, position > 1);
      this.toggle(this.next, this.prev, position < max - 1);
    }

    toggle(button, other, show) {
      if (button.hidden === !show) return;
      if (!show && document.activeElement === button) {
        button.hidden = true;
        if (!other.hidden) other.focus();
        return;
      }
      button.hidden = !show;
    }
  }

  customElements.define('st-home-stories', StHomeStories);
})();
